import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { Mono } from "./NetworkAtoms";

/**
 * Estimated new UK companies formed so far this quarter.
 *
 * Modelled on the three published 2025 quarters and ticked forward in working hours only —
 * Companies House doesn't incorporate companies at 3am, and pretending otherwise would be
 * a lie dressed up as a live feed. Labelled as an estimate, with the inputs printed.
 */
const Q2025 = [211112, 209798, 215982];
const QUARTER_AVG = Math.round(Q2025.reduce((a, b) => a + b, 0) / 3);   // 212,297
const WORKING_DAYS = 65;                                                 // per quarter
const OPEN_H = 8;
const CLOSE_H = 18;
const RATE = QUARTER_AVG / (WORKING_DAYS * (CLOSE_H - OPEN_H) * 3600);   // per working second

const PER_DAY = Math.round(QUARTER_AVG / WORKING_DAYS);
const PER_HOUR = Math.round(PER_DAY / (CLOSE_H - OPEN_H));
const EVERY_SECS = Math.round(1 / RATE);

const isWeekday = (d) => d.getDay() % 6 !== 0;
const isOpenNow = (d = new Date()) => isWeekday(d) && d.getHours() >= OPEN_H && d.getHours() < CLOSE_H;

function workingSecondsThisQuarter(now = new Date()) {
  const start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  let secs = 0;
  const day = new Date(start);
  while (day <= now) {
    if (isWeekday(day)) {
      const from = new Date(day); from.setHours(OPEN_H, 0, 0, 0);
      const to = new Date(day); to.setHours(CLOSE_H, 0, 0, 0);
      const a = Math.max(from.getTime(), start.getTime());
      const b = Math.min(to.getTime(), now.getTime());
      if (b > a) secs += (b - a) / 1000;
    }
    day.setDate(day.getDate() + 1);
    day.setHours(0, 0, 0, 0);
  }
  return secs;
}

const estimate = () => Math.floor(RATE * workingSecondsThisQuarter());

export default function GrowthCounter() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduce = useReducedMotion();
  const base = useMemo(estimate, []);
  const [count, setCount] = useState(base);
  const [open, setOpen] = useState(isOpenNow());

  // count up on reveal, then hold it honest against the clock
  useEffect(() => {
    if (!inView) return undefined;
    if (reduce) {
      setCount(base);
      return undefined;
    }
    let raf;
    let live;
    const t0 = performance.now();
    const D = 1700;
    const step = (t) => {
      const k = Math.min((t - t0) / D, 1);
      setCount(Math.round(base * (1 - (1 - k) ** 3)));
      if (k < 1) raf = requestAnimationFrame(step);
      else live = setInterval(() => { setCount(estimate()); setOpen(isOpenNow()); }, 1000);
    };
    setCount(0);
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); clearInterval(live); };
  }, [inView, reduce, base]);

  const delta = Math.max(count - base, 0);

  return (
    <div ref={ref} data-testid="growth-counter"
         className="relative mt-12 overflow-hidden rounded-2xl border border-[#0055FF]/25 bg-[#0055FF]/[0.05] p-6 sm:p-9">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-24 h-[420px] w-[420px] rounded-full bg-[#0055FF] opacity-[0.16] blur-[120px]" />

      <div className="relative grid lg:grid-cols-12 gap-9 lg:gap-8 items-end">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2.5">
            <span className="relative flex size-2">
              {open && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#7da9ff] opacity-70" />}
              <span className={`relative inline-flex size-2 rounded-full ${open ? "bg-[#7da9ff]" : "bg-slate-600"}`} />
            </span>
            <Mono>{open ? "Counting now · working hours" : `Paused · counting resumes ${OPEN_H}am`}</Mono>
          </div>

          <p data-testid="growth-counter-value"
             className="mt-5 font-cabinet text-[54px] sm:text-[80px] leading-[0.84] tracking-[-0.045em] font-semibold text-white tabular-nums">
            {count.toLocaleString("en-GB")}
          </p>
          <p className="mt-4 text-[15px] sm:text-[16.5px] leading-snug text-slate-200 max-w-md">
            New UK companies formed so far this quarter — every one of them a business that will
            eventually need capital.
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="grid grid-cols-3 gap-4 sm:gap-6">
            {[
              [PER_DAY.toLocaleString("en-GB"), "a working day"],
              [PER_HOUR.toLocaleString("en-GB"), "an hour"],
              [`${EVERY_SECS}s`, "between each one"],
            ].map(([big, small]) => (
              <div key={small}>
                <p className="font-cabinet text-[22px] sm:text-[26px] leading-none tracking-[-0.03em] font-semibold text-white tabular-nums">
                  {big}
                </p>
                <p className="mt-2 text-[11.5px] leading-tight text-slate-400">{small}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-3.5 h-8"
               data-testid="growth-counter-delta">
            <span className="font-mono text-[11px] text-[#7da9ff] tabular-nums">+{delta}</span>
            <span className="text-[11.5px] text-slate-400">since you opened this page</span>
          </div>
        </div>
      </div>

      <p className="relative mt-8 text-[11px] leading-relaxed text-slate-500 max-w-3xl">
        Estimate, not a live feed. Modelled on Companies House quarterly incorporations for 2025
        ({Q2025.map((q) => q.toLocaleString("en-GB")).join(" · ")}) and counted across UK working hours
        only. Shown for market context — it is not a Connexis performance figure.
      </p>
    </div>
  );
}
